import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, ExternalLink, EyeOff, Loader2, RefreshCw, Trash2 } from "lucide-react";

type Row = {
  id: string;
  title: string;
  program_url: string | null;
  next_deadline: string | null;
  verified: boolean;
  opportunity_type: string;
  provider_agency: string | null;
  data_source: string;
  source_ref: string | null;
  cycle: string | null;
  last_verified_at: string | null;
};

const DATA_SOURCES = [
  { value: "grants_gov", label: "grants.gov" },
  { value: "curated", label: "Curated" },
  { value: "sba_api", label: "SBA API" },
  { value: "cdfi_csv", label: "CDFI CSV" },
  { value: "manual", label: "Manual" },
  { value: "__all__", label: "All sources" },
];

const OpportunityReviewTab = () => {
  const qc = useQueryClient();
  const [dataSource, setDataSource] = useState<string>("grants_gov");
  const [status, setStatus] = useState<"unverified" | "verified" | "all">("unverified");
  const [search, setSearch] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin-opportunity-review", dataSource, status],
    queryFn: async () => {
      let q = supabase
        .from("grant_programs")
        .select(
          "id, title, program_url, next_deadline, verified, opportunity_type, provider_agency, data_source, source_ref, cycle, last_verified_at",
        )
        .order("last_verified_at", { ascending: false, nullsFirst: false })
        .limit(300);
      if (dataSource !== "__all__") q = q.eq("data_source", dataSource);
      if (status === "unverified") q = q.eq("verified", false);
      if (status === "verified") q = q.eq("verified", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const filtered = rows.filter((r) =>
    !search.trim()
      ? true
      : (r.title + " " + (r.provider_agency ?? "") + " " + (r.source_ref ?? ""))
          .toLowerCase()
          .includes(search.toLowerCase()),
  );

  const verifyMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.functions.invoke("admin-verify-opportunity", {
        body: { id, action: "verify" },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Verified & published to /opportunities");
      qc.invalidateQueries({ queryKey: ["admin-opportunity-review"] });
      qc.invalidateQueries({ queryKey: ["grant-programs"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unpublishMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.functions.invoke("admin-verify-opportunity", {
        body: { id, action: "unpublish" },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Unpublished");
      qc.invalidateQueries({ queryKey: ["admin-opportunity-review"] });
      qc.invalidateQueries({ queryKey: ["grant-programs"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("grant_programs").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Removed");
      qc.invalidateQueries({ queryKey: ["admin-opportunity-review"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold">Opportunity review queue</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Verify ingested opportunities before they appear on <code>/opportunities</code>. Rows from{" "}
          <code>grants.gov</code> land here unverified after each daily ingest run.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[180px]">
          <label className="text-xs text-muted-foreground block mb-1">Source</label>
          <Select value={dataSource} onValueChange={setDataSource}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATA_SOURCES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <label className="text-xs text-muted-foreground block mb-1">Status</label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unverified">Unverified only</SelectItem>
              <SelectItem value="verified">Verified only</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs text-muted-foreground block mb-1">Search</label>
          <Input
            placeholder="Title, agency, or source ref…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          onClick={() => qc.invalidateQueries({ queryKey: ["admin-opportunity-review"] })}
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="text-xs text-muted-foreground">
        Showing {filtered.length} of {rows.length} loaded rows.
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading queue…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-xl">
          <p className="text-sm">Nothing in this queue.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <div
              key={r.id}
              className="bg-card border border-border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Badge variant={r.verified ? "default" : "secondary"}>
                    {r.verified ? "Published" : "Unverified"}
                  </Badge>
                  {r.provider_agency && <Badge variant="outline">{r.provider_agency}</Badge>}
                  <Badge variant="outline">{r.opportunity_type}</Badge>
                  <span className="text-xs text-muted-foreground">{r.data_source}</span>
                </div>
                <h3 className="font-medium truncate">{r.title}</h3>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-3 mt-1">
                  {r.next_deadline && <span>Deadline: {r.next_deadline}</span>}
                  {r.cycle && <span>Cycle: {r.cycle}</span>}
                  {r.source_ref && <span>Ref: {r.source_ref}</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {r.program_url && (
                  <Button asChild variant="ghost" size="sm">
                    <a href={r.program_url} target="_blank" rel="noreferrer noopener">
                      <ExternalLink className="h-4 w-4 mr-1" /> Open
                    </a>
                  </Button>
                )}
                {!r.verified ? (
                  <Button
                    size="sm"
                    onClick={() => verifyMut.mutate(r.id)}
                    disabled={verifyMut.isPending}
                    className="bg-primary text-primary-foreground"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Verify & publish
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => unpublishMut.mutate(r.id)}
                    disabled={unpublishMut.isPending}
                  >
                    <EyeOff className="h-4 w-4 mr-1" /> Unpublish
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    if (confirm(`Delete "${r.title}"? This cannot be undone.`)) deleteMut.mutate(r.id);
                  }}
                  disabled={deleteMut.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OpportunityReviewTab;